import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Subject, takeUntil } from 'rxjs';
import { ContactService } from 'src/app/core/services/contact';
import { OktaUserRequest, OktaUserService } from '../services/okta-user';

@Component({
  selector: 'app-onboarding-okta-user',
  templateUrl: 'onboarding-okta-user.component.html',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatButtonModule,
    FormsModule,
    MatProgressSpinnerModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingOktaUserComponent implements OnDestroy {
  private fb = inject(FormBuilder);

  onboardingForm = this.fb.group({
    email: ['', [Validators.email]],
    accountNumber: [''],
    username: ['', Validators.required],
  });

  contactErrorMessage = signal(null);

  successMessage = signal(null);

  isSubmitting = signal(false);

  private destroy$ = new Subject<void>();

  private contactService = inject(ContactService);

  private oktaUserService = inject(OktaUserService);

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit(): void {
    this.contactErrorMessage.set(null);
    this.successMessage.set(null);

    if (this.onboardingForm.invalid || this.isSubmitting()) return;
    this.isSubmitting.set(true);

    const { email, username } = this.onboardingForm.value;

    this.contactService
      .getContactByOktaUsernameAndEmail(email, username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contact) => {
          if (!contact) {
            this.contactErrorMessage.set(
              `No contact found for ${email} / ${username}.`,
            );
            return;
          }

          const payload: OktaUserRequest = {
            email: contact.Email,
            login: contact.OktaUsername__c,
            firstName: contact.FirstName,
            lastName: contact.LastName,
            accountId: contact.AccountId,
          };

          this.oktaUserService
            .onboardUser(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.successMessage.set(
                  'User successfully created. An activation email should be received shortly.',
                );
                this.isSubmitting.set(false);
              },
              error: (error) => {
                this.isSubmitting.set(false);
                this.contactErrorMessage.set(
                  `Failed to create user. ${error?.message}`,
                );
                console.error('Failed to create user:', error);
              },
            });
        },
        error: (error) => {
          this.isSubmitting.set(false);
          this.contactErrorMessage.set(
            (error?.message as string) ||
              'An error occurred looking up the contact.',
          );
          console.error('Contact lookup error:', error);
        },
      });
  }
}
